import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PaginationBar } from "@/components/PaginationBar";
import { WidgetError } from "@/components/WidgetError";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { fmtDate } from "@/lib/format";
import { X } from "lucide-react";

/**
 * Audit Log — server-paginated, indexed, filterable.
 * Long JSON details are truncated in the row and shown in a right-side drawer
 * on "View details" so wide payloads never push the whole page sideways.
 */
export default function AuditPage() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [actionOptions, setActionOptions] = useState([]);

  // filters
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [detail, setDetail] = useState(null); // audit row shown in drawer

  useEffect(() => {
    api.get("/audit/actions").then(({ data }) => setActionOptions(data.items || [])).catch(() => {});
  }, []);

  useEffect(() => { setPage(1); }, [action, entityType, dateFrom, dateTo]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = { page, limit: 30 };
    if (action) params.action = action;
    if (entityType) params.entity_type = entityType;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/audit", { params })
       .then(({ data }) => { if (!cancelled) { setItems(data.items); setPages(data.pages); } })
       .catch((err) => { if (!cancelled) setError(err); })
       .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, action, entityType, dateFrom, dateTo, reloadKey]);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);

  const entityTypes = useMemo(() => {
    const s = new Set();
    items.forEach((a) => a.entity_type && s.add(a.entity_type));
    return Array.from(s).sort();
  }, [items]);

  const clearFilters = () => {
    setAction(""); setEntityType(""); setDateFrom(""); setDateTo("");
  };

  return (
    <div className="min-w-0">
      <div className="mb-6">
        <div className="label-overline">Management</div>
        <h1 className="text-3xl font-black tracking-tight mt-1">Audit Log</h1>
        <p className="text-slate-600 text-sm mt-1">
          Append-only. Latest events shown first. Use filters to narrow.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded p-4 mb-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        <div>
          <Label className="label-overline">Action</Label>
          <select data-testid="audit-filter-action"
                  value={action} onChange={(e) => setAction(e.target.value)}
                  className="mt-1 h-9 w-full border border-slate-300 rounded px-2 text-xs font-mono">
            <option value="">All</option>
            {actionOptions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <Label className="label-overline">Entity type</Label>
          <select data-testid="audit-filter-entity"
                  value={entityType} onChange={(e) => setEntityType(e.target.value)}
                  className="mt-1 h-9 w-full border border-slate-300 rounded px-2 text-xs font-mono">
            <option value="">All</option>
            {["order", "invoice", "credit_note", "payment", "statement", "customer", "tenant",
              "service_type", "user", "ledger", "opening_balance"].map((et) =>
              <option key={et} value={et}>{et}</option>)}
          </select>
        </div>
        <div>
          <Label className="label-overline">Date from</Label>
          <Input data-testid="audit-filter-from" type="date" value={dateFrom}
                 onChange={(e) => setDateFrom(e.target.value)}
                 className="mt-1 h-9 rounded text-xs font-mono" />
        </div>
        <div>
          <Label className="label-overline">Date to</Label>
          <Input data-testid="audit-filter-to" type="date" value={dateTo}
                 onChange={(e) => setDateTo(e.target.value)}
                 className="mt-1 h-9 rounded text-xs font-mono" />
        </div>
        <div className="flex items-end">
          <Button type="button" variant="outline" data-testid="audit-filter-clear"
                  onClick={clearFilters}
                  className="rounded h-9 w-full">Clear</Button>
        </div>
      </div>

      {/* Table (contained) */}
      <div className="bg-white border border-slate-200 rounded overflow-hidden">
        {error ? (
          <div className="p-4">
            <WidgetError error={error} onRetry={retry} title="Could not load audit log" testId="audit-error" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl w-full text-sm min-w-[720px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="label-overline text-slate-600">
                  <th className="text-left px-4 py-3 whitespace-nowrap">Time</th>
                  <th className="text-left px-4 py-3 whitespace-nowrap">Actor</th>
                  <th className="text-left px-4 py-3 whitespace-nowrap">Action</th>
                  <th className="text-left px-4 py-3 whitespace-nowrap">Entity</th>
                  <th className="text-left px-4 py-3">Details</th>
                  <th className="text-left px-4 py-3 whitespace-nowrap"></th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No audit events match these filters</td></tr>
                )}
                {items.map((a) => {
                  const preview = a.after ? JSON.stringify(a.after) : "";
                  return (
                    <tr key={a.id} className="border-b border-slate-100 align-top" data-testid={`audit-row-${a.action}`}>
                      <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{fmtDate(a.created_at)}</td>
                      <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{a.actor_role || "—"}</td>
                      <td className="px-4 py-2 font-mono whitespace-nowrap">{a.action}</td>
                      <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">
                        {a.entity_type}
                        {a.entity_id && <span className="text-slate-400"> · {a.entity_id.slice(0, 8)}</span>}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-600 max-w-[280px]">
                        <div className="truncate">{preview || "—"}</div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {a.after && (
                          <button data-testid={`audit-view-${a.id}`}
                                  onClick={() => setDetail(a)}
                                  className="text-blue-700 hover:underline text-xs font-semibold">
                            View details
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <PaginationBar page={page} pages={pages} onChange={setPage} />
      </div>

      {/* Details drawer — contained scrollable code block */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto"
                      data-testid="audit-detail-drawer">
          <SheetHeader>
            <SheetTitle className="font-black">Audit event</SheetTitle>
            <SheetDescription className="sr-only">
              Full details of the selected audit event, including before/after payloads.
            </SheetDescription>
          </SheetHeader>
          {detail && (
            <div className="mt-4 space-y-3 text-sm">
              <div>
                <div className="label-overline">Time</div>
                <div className="font-mono text-xs">{fmtDate(detail.created_at)}</div>
              </div>
              <div>
                <div className="label-overline">Action</div>
                <div className="font-mono">{detail.action}</div>
              </div>
              <div>
                <div className="label-overline">Actor</div>
                <div className="font-mono text-xs">{detail.actor_role} · {detail.actor_id?.slice(0, 8)}</div>
              </div>
              <div>
                <div className="label-overline">Entity</div>
                <div className="font-mono text-xs break-all">
                  {detail.entity_type} · {detail.entity_id}
                </div>
              </div>
              <div>
                <div className="label-overline mb-1">Details (before → after)</div>
                {detail.before && (
                  <pre className="bg-slate-50 border border-slate-200 rounded p-3 text-[11px] font-mono
                                  max-h-64 overflow-auto whitespace-pre-wrap break-all">
{JSON.stringify(detail.before, null, 2)}
                  </pre>
                )}
                <pre className="mt-2 bg-slate-900 text-slate-100 rounded p-3 text-[11px] font-mono
                                max-h-96 overflow-auto whitespace-pre-wrap break-all">
{JSON.stringify(detail.after ?? {}, null, 2)}
                </pre>
              </div>
              <button onClick={() => setDetail(null)}
                      data-testid="audit-detail-close"
                      className="mt-2 inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900">
                <X size={12} /> Close
              </button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
